import { Product } from '@/domain/products/types';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { Tab, Tabs } from '@andritzot/metris-web-ui/navigation/tabs';
import { Card, CardContent } from '@andritzot/metris-web-ui/surfaces/card';
import { useState } from 'react';

import { CountriesContent } from './CountriesContent';
import { ProductCountriesMap } from './ProductCountriesMap';
import { SecurityContent } from './SecurityContent';
import { useProductCountriesMapData } from './useProductCountriesMapData';

interface OverviewTabsWithMiscellaneousChipsContentProps {
  product: Product;
  isLoggedIn: boolean;
}

export const OverviewTabsWithMiscellaneousChips = ({
  product,
  isLoggedIn,
}: OverviewTabsWithMiscellaneousChipsContentProps) => {
  const installedBasesData = useProductCountriesMapData(product.idProduct, isLoggedIn);

  const tabs = [
    {
      label: 'INSTALLED BASES',
      hasValue: installedBasesData.length > 0,
      content: (
        <ProductCountriesMap bubbleData={installedBasesData} isLoggedIn={isLoggedIn} productName={product.product} />
      ),
    },
    {
      label: 'COUNTRIES',
      hasValue: isLoggedIn && product.countries?.length > 0,
      content: <CountriesContent productCountries={product.countries} />,
    },
    {
      label: 'SECURITY',
      hasValue: isLoggedIn && product.capabilitySecurityLevel != null,
      content: <SecurityContent productSecurityLevel={product.capabilitySecurityLevel as number} />,
    },
  ].filter((tab) => tab.hasValue);

  // Tab state is keyed by label, not index: INSTALLED BASES only appears once its query resolves,
  // and an index would silently point at a different tab when the list changes length.
  const [activeLabel, setActiveLabel] = useState<string | null>(null);

  const activeTab = tabs.find((tab) => tab.label === activeLabel) ?? tabs.at(0);

  if (!activeTab) {
    return null;
  }

  return (
    <Card sx={{ my: 4, backgroundColor: '#fff', width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab.label}
          onChange={(_, v: string) => setActiveLabel(v)}
          variant="scrollable"
          scrollButtons="auto"
          type="secondary"
        >
          {tabs.map((tab) => (
            <Tab key={tab.label} label={tab.label} value={tab.label} />
          ))}
        </Tabs>
      </Box>
      <CardContent>
        <Box role="tabpanel">{activeTab.content}</Box>
      </CardContent>
    </Card>
  );
};
